/* sankey-diagram.ts */

/* Chroma */
import chroma from "chroma-js";

/* d3.js */
import * as d3 from "d3";
import { sankeyLinkHorizontal } from "d3-sankey";

/* Helper */
import {
  createAnnotations,
  setTooltipData,
  createColors,
  createGradient,
  highlightFlow,
  resetHighlight,
  highlightConnectedNode,
  createTransformedLabel,
} from "src/components/charts/sankey-diagram/helper";

/* Utils */
import { createChartTooltip } from "src/utils/helper/chart-tooltip";
import { resolveGroupForCombinedName, getBaseName, getSuffix } from "src/utils/helper/treatment-patterns/helper";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import {
  CustomSankeyNode,
  CustomSankeyLink,
  CustomSankeyGraph,
} from "src/shared/interfaces/charts/SankeyDiagram";

import {
  ChartTooltipRef,
  SankeyTooltipProps,
} from "src/shared/interfaces/ChartTooltip";

import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { CustomFeature } from "src/shared/constants/common/CustomFeature";

const VIEW_BOX_PADDING_TOP = 52;
const VIEW_BOX_PADDING_BOTTOM = 108;
const MIN_NODE_HEIGHT: number = 5;

export const createViewBox = (
  svg: SVGSelection,
  dimensions: ChartDimensionsProps
) => {
  const { height, width } = dimensions;

  svg
    .attr(
      "viewBox",
      `0 -${VIEW_BOX_PADDING_TOP} ${width} ${
        height +
        VIEW_BOX_PADDING_TOP +
        VIEW_BOX_PADDING_BOTTOM
      }`
    )
    .attr("preserveAspectRatio", "xMinYMin meet");
};

const createNodes = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  onNodeClick?: (node: CustomSankeyNode) => void,
  customFeature?: CustomFeature,
  treatmentGroupMap?: Record<string, string>,
  expandedGroups?: string[],
  onGroupToggle?: (groupNodeName: string) => void
) => {
  const getGroupNodeName = (nodeName: string): string | null => {
    if (!treatmentGroupMap) return null;
    const baseName = getBaseName(nodeName);
    const suffix = getSuffix(nodeName);
    const groupBase =
      treatmentGroupMap[baseName] ??
      (baseName.includes(" + ")
        ? resolveGroupForCombinedName(baseName, treatmentGroupMap)
        : undefined);
    if (!groupBase) return null;
    return suffix ? `${groupBase} ${suffix}` : groupBase;
  };

  svg
    .selectAll<SVGRectElement, CustomSankeyNode>(".node")
    .data(data.nodes)
    .join("rect")
    .attr("class", "node")
    .attr("x", (d) => d.x0)
    .attr("y", (d) => {
      const realHeight = d.y1! - d.y0!;
      const displayHeight = Math.max(
        realHeight,
        MIN_NODE_HEIGHT
      );
      const center = (d.y0! + d.y1!) / 2;
      return center - displayHeight / 2;
    })
    .attr("height", (d) => {
      const realHeight = d.y1! - d.y0!;
      return Math.max(realHeight, MIN_NODE_HEIGHT);
    })
    .attr("width", data.nodes[0].x1! - data.nodes[0].x0!)
    .attr("fill", (d) =>
      createColors(d.name, customFeature)
    )
    .attr("cursor", (d) => {
      if (d.isTreatmentGroup) return "zoom-in";
      const groupNodeName = getGroupNodeName(d.name);
      if (groupNodeName && expandedGroups?.includes(groupNodeName)) return "zoom-out";
      return "crosshair";
    })
    .on(
      "click",
      (event: MouseEvent, d: CustomSankeyNode) => {
        // Group node: expand it
        if (d.isTreatmentGroup && onGroupToggle) {
          onGroupToggle(d.name);
          return;
        }

        // Member node of expanded group: collapse it
        if (onGroupToggle && expandedGroups) {
          const groupNodeName = getGroupNodeName(d.name);
          if (groupNodeName && expandedGroups.includes(groupNodeName)) {
            onGroupToggle(groupNodeName);
            return;
          }
        }

        const svgElement = (
          event.currentTarget as SVGElement
        ).closest("svg");
        if (!svgElement) return;

        const localSvg = d3.select<SVGSVGElement, unknown>(
          svgElement
        );
        const diagramId = localSvg.attr("data-diagram-id");
        if (!diagramId) return;

        const active = localSvg.attr("data-active-node-id");
        const clickedId = `${d.name}@${d.depth}`;

        if (active === clickedId) {
          localSvg.attr("data-active-node-id", null);
          resetHighlight(localSvg, diagramId);
        } else {
          localSvg.attr("data-active-node-id", clickedId);
          localSvg.attr("data-active-flow-id", null);
          highlightConnectedNode(localSvg, d, diagramId);
        }

        if (onNodeClick && d.name.startsWith("Other")) {
          onNodeClick(d);
        }
      }
    );
};

const createLinks = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  diagramId: string,
  customFeature?: CustomFeature
) => {
  createGradient(svg, data, diagramId, customFeature);

  svg
    .selectAll<SVGPathElement, CustomSankeyLink>(".link")
    .data(data.links)
    .join("path")
    .attr("class", "link")
    .attr("d", sankeyLinkHorizontal())
    .attr(
      "stroke",
      (_, i) => `url(#${diagramId}-gradient-${i})`
    )
    .attr("stroke-opacity", 0.5)
    .attr("stroke-width", (d) => Math.max(d.width!, 5))
    .attr("cursor", "cell")
    .attr("fill", "none")
    .on(
      "mousemove",
      (event: MouseEvent, d: CustomSankeyLink) => {
        const target = event.target as SVGElement;
        d3.select(target).attr("stroke-opacity", 0.75);

        const tooltipData: SankeyTooltipProps = {
          source: d.source.name,
          target: d.target.name,
          value: d.value,
        };

        createChartTooltip(
          tooltipRef,
          () => setTooltipData(tooltipData, customFeature),
          svg,
          event,
          {
            margin: -52,
          }
        );
      }
    )
    .on("mouseleave", (event: MouseEvent) => {
      const target = event.target as SVGElement;
      d3.select(target).attr("stroke-opacity", 0.5);
      if (tooltipRef.current)
        tooltipRef.current.hideTooltip();
    })
    .on(
      "click",
      (event: MouseEvent, d: CustomSankeyLink) => {
        const sourceLabel = d.source.name;
        const targetLabel = d.target.name;
        const flowId = `${sourceLabel}@${d.source.depth}→${targetLabel}@${d.target.depth}`;

        const targetElement =
          event.currentTarget as Element;
        const svgElement = targetElement.closest("svg");

        if (!svgElement) return;

        const svg = d3.select<SVGSVGElement, unknown>(
          svgElement
        );
        const diagramId = svg.attr("data-diagram-id");

        const isActive =
          svg.attr("data-active-flow-id") === flowId;

        if (isActive) {
          svg.attr("data-active-flow-id", null);
          resetHighlight(svg, diagramId!);
        } else {
          svg.attr("data-active-flow-id", flowId);
          highlightFlow(svg, flowId, diagramId!);
        }
      }
    );
};

const createLabels = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  customFeature?: CustomFeature,
  groupMap?: Record<string, string>,
  expandedGroups?: string[],
  onGroupToggle?: (groupNodeName: string) => void
) => {
  svg
    .selectAll<SVGTextElement, CustomSankeyNode>(".label")
    .data(data.nodes, (d) => d.name)
    .join("text")
    .attr("class", "label")
    .attr("x", (d) => (d.x0 === 0 ? d.x1 + 11 : d.x0 - 10))
    .attr("y", (d) => (d.y0 + d.y1) / 2)
    .attr("dy", "0.35em")
    .attr("text-anchor", (d) =>
      d.x0 === 0 ? "start" : "end"
    )
    .text((d) =>
      createTransformedLabel(d.name, customFeature)
    )
    .attr("font-size", "14px")
    .each((d, i, nodes) => {
      const el = d3.select(nodes[i]);

      const color = chroma(
        createColors(d.name, customFeature)
      )
        .darken(0.75)
        .hex();
      el.attr("data-original-fill", color).attr(
        "fill",
        color
      );

      if (!groupMap || !onGroupToggle) return;

      const suffix = getSuffix(d.name);
      const baseName = getBaseName(d.name);
      const isGroupNode = Boolean(d.isTreatmentGroup);
      const memberGroupBase = !isGroupNode
        ? (groupMap[baseName] ??
            (baseName.includes(" + ")
              ? resolveGroupForCombinedName(baseName, groupMap)
              : undefined))
        : undefined;

      const transformed = createTransformedLabel(
        d.name,
        customFeature
      );

      if (isGroupNode) {
        el.text(`▶ ${transformed}`)
          .attr("cursor", "zoom-in")
          .on("click", (event: MouseEvent) => {
            event.stopPropagation();
            onGroupToggle(d.name);
          });
      } else if (memberGroupBase) {
        const groupNodeName = suffix
          ? `${memberGroupBase} ${suffix}`
          : memberGroupBase;
        if (expandedGroups?.includes(groupNodeName)) {
          el.text(`▼ ${transformed}`)
            .attr("cursor", "zoom-out")
            .on("click", (event: MouseEvent) => {
              event.stopPropagation();
              onGroupToggle(groupNodeName);
            });
        }
      }
    });
};

/**
 * 'createSankeyDiagram' orchestrates
 * all functions that are required to
 * create the Sankey diagram.
 */
export const createSankeyDiagram = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  dimension: ChartDimensionsProps,
  maxDepth: number,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  diagramId: string,
  customFeature?: CustomFeature,
  onNodeClick?: (node: CustomSankeyNode) => void,
  onGroupToggle?: (groupNodeName: string) => void,
  treatmentGroupMap?: Record<string, string>,
  expandedGroups?: string[]
) => {
  if (!svg || !data) return;

  createViewBox(svg, dimension);
  createLinks(
    svg,
    data,
    tooltipRef,
    diagramId,
    customFeature
  );
  createNodes(svg, data, onNodeClick, customFeature, treatmentGroupMap, expandedGroups, onGroupToggle);
  createLabels(
    svg,
    data,
    customFeature,
    treatmentGroupMap,
    expandedGroups,
    onGroupToggle
  );
  createAnnotations(svg, data, maxDepth, dimension);
};
